import React from 'react';
import Logo from './Logo';
import { openCookieSettings } from './CookieConsent';
// categories.json is the build-time output of getUniqueCategories(fontData) —
// see scripts/generate-categories.js. The footer only needs the names, and the
// full font dataset is 264KB, which landed in the eagerly-loaded main bundle.
import categories from '../data/categories.json';
import { formatCategoryName, getCategoryPath } from '../seoConfig';

const FacebookIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12Z" />
  </svg>
);

const InstagramIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

// Inlined rather than imported from react-icons/fa: that barrel is a single
// 1.3MB module, so one icon pulled the whole set into the main bundle.
const PinterestIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
    <path d="M204 6.5C101.4 6.5 0 74.9 0 185.6 0 256 39.6 296 63.6 296c9.9 0 15.6-27.6 15.6-35.4 0-9.3-23.7-29.1-23.7-67.8 0-80.4 61.2-137.4 140.4-137.4 68.1 0 118.5 38.7 118.5 109.8 0 53.1-21.3 152.7-90.3 152.7-24.9 0-46.2-18-46.2-43.8 0-37.8 26.4-74.4 26.4-113.4 0-66.2-93.9-54.2-93.9 25.8 0 16.8 2.1 35.4 9.6 50.7-13.8 59.4-42 147.9-42 209.1 0 18.9 2.7 37.5 4.5 56.4 3.4 3.8 1.7 3.4 6.9 1.5 50.4-69 48.6-82.5 71.4-172.8 12.3 23.4 44.1 36 69.3 36 106.2 0 153.9-103.5 153.9-196.8C384 71.3 298.2 6.5 204 6.5z" />
  </svg>
);

const Footer = () => {
  const mid = Math.ceil(categories.length / 2);
  const leftCats  = categories.slice(0, mid);
  const rightCats = categories.slice(mid);

  const link = 'text-[0.72rem] text-[var(--color-muted)] transition-colors duration-150 hover:text-[var(--color-primary)]';
  const heading = 'mb-3 text-[0.62rem] font-black uppercase tracking-[0.13em] text-[var(--color-text)]';

  return (
    <footer className="bg-[var(--color-bg)] pb-5 pt-2">
      <div className="site-container">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-[0_4px_20px_rgba(109,40,217,0.06)]">

          {/* ── Top: brand + social ── */}
          <div className="flex flex-col gap-4 border-b border-[var(--color-border)] px-5 py-5 sm:flex-row sm:items-center sm:justify-between md:px-8 md:py-6">
            <div>
              <Logo imageClassName="h-8 w-auto" />
              <p className="mt-2 max-w-[280px] text-[0.72rem] leading-relaxed text-[var(--color-muted)]">
                Free Unicode font generator — copy and paste stylish text for Instagram, TikTok, Discord, WhatsApp and more.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {[
                { href: 'https://www.facebook.com/profile.php?id=61590911693732', Icon: FacebookIcon, label: 'Facebook' },
                { href: 'https://www.instagram.com/fontifytext/',                 Icon: InstagramIcon, label: 'Instagram' },
                { href: 'https://www.pinterest.com/fontifytext/',                 Icon: PinterestIcon, label: 'Pinterest' },
              ].map(({ href, Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="me noopener noreferrer" aria-label={label}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-muted)] transition-all hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] hover:-translate-y-0.5">
                  <Icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* ── Links: 3-col on desktop ── */}
          <div className="grid grid-cols-1 gap-7 px-5 py-6 sm:grid-cols-3 md:px-8 md:py-7">

            {/* Generators — 2 inner columns */}
            <div className="sm:col-span-1 md:col-span-1">
              <p className={heading}>Font Generators</p>
              <div className="grid grid-cols-2 gap-x-4">
                <ul className="space-y-2">
                  {leftCats.map((cat) => (
                    <li key={cat}>
                      <a href={getCategoryPath(cat)} className={link}>
                        {formatCategoryName(cat)}
                      </a>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {rightCats.map((cat) => (
                    <li key={cat}>
                      <a href={getCategoryPath(cat)} className={link}>
                        {formatCategoryName(cat)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Company */}
            <nav aria-label="Company links">
              <p className={heading}>Company</p>
              <ul className="space-y-2">
                {[
                  ['/',          'Home'],
                  ['/fonts/',    'Font Generators'],
                  ['/backwards-text/', 'Backwards Text'],
                  ['/blog/',     'Blog'],
                  ['/unicode-symbols/', 'Symbols'],
                  ['/kaomoji/',  'Kaomoji'],
                  ['/lenny-faces/', 'Lenny Faces'],
                  ['/dot-art-generator/', 'Dot Art Generator'],
                  ['/ascii-art-generator/', 'ASCII Art Generator'],
                  ['/username-generator/', 'Username Generator'],
                  ['/number-font-generator/', 'Number Font Generator'],
                  ['/tattoo-font-generator/', 'Tattoo Font Generator'],
                  ['/case-converter/', 'Case Converter'],
                  ['/word-counter/', 'Word Counter'],
                  ['/text-styler/', 'Text Styler'],
                  ['/font-image-generator/', 'Font Image Generator'],
                  ['/text-repeater/', 'Text Repeater'],
                  ['/wingdings-translator/', 'Wingdings Translator'],
                  ['/monogram-maker/', 'Monogram Maker'],
                  ['/fullscreen-text/', 'Full Screen Text'],
                  ['/unicode-font-compatibility/', 'Compatibility Guide'],
                  ['/about/',    'About Us'],
                  ['/about/#editorial-standards', 'Editorial Standards'],
                  ['/contact/',  'Contact'],
                ].map(([href, label]) => (
                  <li key={href}><a href={href} className={link}>{label}</a></li>
                ))}
              </ul>
            </nav>

            {/* Legal */}
            <nav aria-label="Legal links">
              <p className={heading}>Legal</p>
              <ul className="space-y-2">
                {[
                  ['/privacy-policy/',   'Privacy Policy'],
                  ['/cookie-policy/',     'Cookie Policy'],
                  ['/terms-conditions/', 'Terms & Conditions'],
                ].map(([href, label]) => (
                  <li key={href}><a href={href} className={link}>{label}</a></li>
                ))}
                {/* Withdrawing consent has to be as easy as giving it. Accept
                    and Reject both stored a choice and the banner never came
                    back, on any page, so until this there was no way to change
                    one. */}
                <li>
                  <button type="button" onClick={openCookieSettings} className={link}>
                    Cookie settings
                  </button>
                </li>
              </ul>
            </nav>

          </div>

          {/* ── Bottom bar ── */}
          <div className="flex flex-col items-center gap-1 border-t border-[var(--color-border)] px-5 py-3 text-center sm:flex-row sm:justify-between sm:text-left md:px-8">
            <p className="text-[0.65rem] text-[var(--color-muted)]">© 2026 FontifyText. All rights reserved.</p>
            <p className="text-[0.65rem] text-[var(--color-muted)]">
              Designed &amp; developed by{' '}
              <a href="https://appjingle.com/" target="_blank" rel="noopener noreferrer nofollow"
                className="font-semibold text-[var(--color-primary)] hover:underline">
                AppJingle Solutions
              </a>
            </p>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
